import {
  ApdmImportFailure,
  ApdmImportOutcome,
  ApdmImportQuestion,
  ApdmImportSkipReason,
  ApdmImportStatus,
  ApdmImportView,
  ApdmReadingGap,
} from '@euler/model/apdm';

/** Where an import is while the system works on it by itself. */
export enum ApdmImportStage {
  Reading = 'reading',
  Recognizing = 'recognizing',
  Organizing = 'organizing',
}

/** What the import found, shaped for the summary. */
export interface ApdmImportSummary {
  visits: number;
  vehicles: number;
  dateRange: { from: string; through: string } | null;
  dues: ApdmImportOutcome['dues'];
  /** Routine sub-categories, most serviced first. */
  routine: Array<{ category: string; documents: number }>;
  /** The busiest months, busiest first. */
  busiestMonths: Array<{ month: string; visits: number }>;
  leftOut: {
    rows: number;
    totalRows: number;
    byReason: Array<{ reason: ApdmImportSkipReason; rows: number }>;
    washOnly: number;
    notCompleted: number;
    unclassifiedDocuments: number;
    topUnknownItems: Array<{ text: string; count: number }>;
  };
  /** Too much was left out: show the details without being asked. */
  detailsOpen: boolean;
  /** The file had no status column: every row counts as finished work. */
  completionAssumed: boolean;
  /** The file lists vehicles: each row is one last visit, work unknown. */
  vehicleList: boolean;
  /** The file lists customers by plate alone: no visits. */
  contactList: boolean;
}

interface ImportBase {
  id: string;
  fileName: string;
  createdAt: string;
}

export type ApdmImportState =
  | (ImportBase & { kind: 'working'; stage: ApdmImportStage })
  | (ImportBase & { kind: 'question'; question: ApdmImportQuestion })
  | (ImportBase & { kind: 'ready'; summary: ApdmImportSummary })
  | (ImportBase & { kind: 'publishing'; summary: ApdmImportSummary })
  | (ImportBase & { kind: 'published'; summary: ApdmImportSummary })
  | (ImportBase & {
      kind: 'failed';
      /** Publishing stopped part-way and can start again. */
      restartable: boolean;
      summary: ApdmImportSummary | null;
    })
  | (ImportBase & { kind: 'deleted' });

const kBusiestMonths = 3;

function summaryOf(view: ApdmImportView): ApdmImportSummary | null {
  const outcome = view.outcome;
  if (!outcome) return null;
  const byReason = (
    Object.entries(outcome.rows.skipped) as Array<
      [ApdmImportSkipReason, number | undefined]
    >
  )
    .map(([reason, rows]) => ({ reason, rows: rows ?? 0 }))
    .filter(entry => entry.rows > 0)
    .sort((left, right) => right.rows - left.rows);
  return {
    visits: outcome.documents.visits,
    vehicles: outcome.vehicles,
    dateRange: outcome.dateRange,
    dues: outcome.dues,
    routine: Object.entries(outcome.routine)
      .map(([category, documents]) => ({ category, documents }))
      .sort((left, right) => right.documents - left.documents),
    busiestMonths: [...outcome.months]
      .sort((left, right) => right.visits - left.visits)
      .slice(0, kBusiestMonths),
    leftOut: {
      rows: byReason.reduce((sum, entry) => sum + entry.rows, 0),
      totalRows: outcome.rows.total,
      byReason,
      washOnly: outcome.documents.washOnly,
      notCompleted: outcome.rows.notCompleted,
      unclassifiedDocuments: outcome.documents.withUnknownItem,
      topUnknownItems: outcome.items.topUnknown,
    },
    detailsOpen: view.attention?.open ?? false,
    completionAssumed: outcome.completionAssumed,
    vehicleList: outcome.vehicleList,
    contactList: outcome.contactList,
  };
}

/** The server's view of an import, as the app reasons about it. */
export function apdmImportStateFromView(view: ApdmImportView): ApdmImportState {
  const base = {
    id: view.id,
    fileName: view.fileName,
    createdAt: view.createdAt,
  };
  const summary = summaryOf(view);
  switch (view.status) {
    case ApdmImportStatus.Uploaded:
      return { ...base, kind: 'working', stage: ApdmImportStage.Reading };
    case ApdmImportStatus.Summarized:
      return { ...base, kind: 'working', stage: ApdmImportStage.Recognizing };
    case ApdmImportStatus.Read:
      return { ...base, kind: 'working', stage: ApdmImportStage.Organizing };
    case ApdmImportStatus.NeedsAnswer:
      return view.question
        ? { ...base, kind: 'question', question: view.question }
        : { ...base, kind: 'failed', restartable: false, summary: null };
    case ApdmImportStatus.Ready:
      return summary
        ? { ...base, kind: 'ready', summary }
        : { ...base, kind: 'failed', restartable: false, summary: null };
    case ApdmImportStatus.Publishing:
      return summary
        ? { ...base, kind: 'publishing', summary }
        : { ...base, kind: 'failed', restartable: false, summary: null };
    case ApdmImportStatus.Published:
      return summary
        ? { ...base, kind: 'published', summary }
        : { ...base, kind: 'failed', restartable: false, summary: null };
    case ApdmImportStatus.Deleted:
      return { ...base, kind: 'deleted' };
    default:
      return {
        ...base,
        kind: 'failed',
        restartable: view.failure === ApdmImportFailure.Publication,
        summary,
      };
  }
}

/** The system is still working: keep asking the server. */
export function isApdmImportSettled(state: ApdmImportState): boolean {
  return state.kind !== 'working' && state.kind !== 'publishing';
}

/** Fields the file has no column for: no answer can be given. */
export function unanswerableGaps(
  question: ApdmImportQuestion,
): ApdmReadingGap[] {
  return question.gaps.filter(
    gap => !question.columns.some(column => column.fits.includes(gap)),
  );
}
